import { ModelPerformanceRow } from '../../queries/modelPerformance';

export type TrackSegment = 'all' | 'metro' | 'provincial' | 'country';
type Bucket = Exclude<TrackSegment, 'all'> | 'other';

/** Maps a raw tracks.type into the metro / provincial / country buckets. */
export function bucketTrackType(raw: string): Bucket {
  const t = (raw ?? '').toLowerCase();
  if (t.includes('metro')) return 'metro';
  if (t.startsWith('provin')) return 'provincial';
  if (t.includes('country')) return 'country';
  return 'other';
}

export interface ModelAggregate {
  key: string;
  ai_model: string;
  ai_version: string;
  topRaces: number;
  topStaked: number;
  topWinners: number;
  topReturn: number;
  valueRaces: number;
  valueStaked: number;
  valueWinners: number;
  valueReturn: number;
  allWinners: number;
}

const num = (v: number | string): number => Number(v) || 0;

/** ROI % on an SP, unit-flat-stake basis; NaN when no races were assessed. */
export function roiPct(totalReturn: number, races: number): number {
  return races > 0 ? ((totalReturn - races) / races) * 100 : NaN;
}

/**
 * Sums the raw per-(model, track type) rows into per-model totals for the chosen
 * segment ('all' or one track-type bucket), ordered best-first by the tuning
 * rule: top-pick winners → top-pick ROI → total winners → value winners.
 */
export function aggregateModels(rows: ModelPerformanceRow[], segment: TrackSegment): ModelAggregate[] {
  const byModel = new Map<string, ModelAggregate>();
  for (const r of rows) {
    if (segment !== 'all' && bucketTrackType(r.track_type) !== segment) continue;
    const key = [r.ai_model, r.ai_version].filter(Boolean).join(' ') || 'Unknown';
    const agg = byModel.get(key) ?? {
      key, ai_model: r.ai_model, ai_version: r.ai_version,
      topRaces: 0, topStaked: 0, topWinners: 0, topReturn: 0, valueRaces: 0, valueStaked: 0, valueWinners: 0, valueReturn: 0, allWinners: 0,
    };
    agg.topRaces += num(r.top_1_races);
    agg.topStaked += num(r.top_1_staked);
    agg.topWinners += num(r.top_1_winners);
    agg.topReturn += num(r.top_1_return);
    agg.valueRaces += num(r.value_races);
    agg.valueStaked += num(r.value_staked);
    agg.valueWinners += num(r.value_winners);
    agg.valueReturn += num(r.value_return);
    agg.allWinners += num(r.all_picks_winners);
    byModel.set(key, agg);
  }
  return Array.from(byModel.values()).sort(
    (a, b) =>
      b.topWinners - a.topWinners ||
      roiCompare(b, a) ||
      b.allWinners - a.allWinners ||
      b.valueWinners - a.valueWinners ||
      valueRoiCompare(b, a),
  );
}

const roiCompare = (a: ModelAggregate, b: ModelAggregate) => (roiPct(a.topReturn, a.topStaked) || -Infinity) - (roiPct(b.topReturn, b.topStaked) || -Infinity);
const valueRoiCompare = (a: ModelAggregate, b: ModelAggregate) => (roiPct(a.valueReturn, a.valueStaked) || -Infinity) - (roiPct(b.valueReturn, b.valueStaked) || -Infinity);

/** Which track-type segments actually have data, so the panel only offers real tabs. */
export function presentSegments(rows: ModelPerformanceRow[]): TrackSegment[] {
  const buckets = new Set(rows.map((r) => bucketTrackType(r.track_type)));
  return (['metro', 'provincial', 'country'] as const).filter((s) => buckets.has(s));
}
